// src/views/whatsappInbox/whatsappinbox.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../../components/header";
import { Grid, Spin } from "antd";
import { toast } from "react-hot-toast";
import logger, { Logger } from '../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchConversations,
  selectAllConversations,
  selectSelectedConversation,
  selectConversationsStatus,
  selectTotalConversations,
  setSelectedConversation,
  updateLastMessage,
  deleteConversation,
  clearSelectedConversation,
  assignConversationToUser
} from "../../store/slices/conversationsSlice";

import {
  fetchMessages,
  selectLead,
  addMessage,
  selectReplyContext,
  selectHasMoreMessages,
  selectLoadingOlderMessages,
  fetchOlderMessages
} from "../../store/slices/messagesSlice";

import {
  setSearchInChat,
  selectSearchInChat,
  setShowChatDetails,
  selectShowChatDetails,

  setLoadingState,
  // New Redux UI actions
  setOpenForm,
  selectOpenForm,
  setShowFbButton,
  selectShowFbButton,
  
  selectAssignLeadHeaderTitle,
  setOpenLeadDrawer,
  selectOpenLeadDrawer,
  
} from "../../store/slices/uiSlice";

import {
  selectWhatsappPhoneId,
  selectTeamMembers,
  selectIsWhatsappIntegrated,
  setCurrentUser
} from "../../store/slices/userSlice";

import {
  joinRoomRequest,
  leaveRoomRequest
} from "../../store/slices/socketSlice";

// Component imports
import InboxChatHeadRedux from "./InboxChatHeadRedux";
import IncomingMsgRedux from './IncomingMsgRedux';
import ConversationBody from './components/ConversationBody'; // New consolidated component
import TypingActionAreaRedux from './TypingActionAreaRedux';
import ChatLeftMenuRedux from './ChatLeftMenuRedux';


import { assignChatToUser, assignLeadToUser, loadStatusData, scrollToBottom as scrollChatToBottom } from "./utils/whatsappInboxUtil";


import AddChat from "./AddChat";
import "./whatsapp.css";
import "./whatsappInboxOverrides.css"; // New override CSS for refactored UI
import { EmptyChatScreen, LoaderContainer } from "./style";



// Service imports - will gradually replace with Redux
import { assignConversation } from "../../services/whatsapp";
import { assignLeadInList, getLeadDetail } from "../../services/leadService";

// Other component imports
import TaskDrawer from "../common/TaskDrawer";
import CreateTaskForm from "../../components/createTaskForm";
import CreateNoteForm from "../../components/createNoteForm";
import GlobalDrawer from "../../components/GlobalDrawer";
import UltimatePlanInfo from "../../components/UltimatePlanInfo/UltimatePlanInfo";
import FacebookIntegration from "../../components/FacebookIntegration";

import { LeadDataI } from "../leads/leads";
import MediaPreview from "./components/MediaPreview";
import LeadDetails from "../leads/leadDetails/leadDetails";
import DrawerComponent from "../../components/drawer";
import { leadFileList, setActivityList, setLeadTaskList, setNote, updateSingleLead } from "../../actions/actions";

// Import status and label popup components
import SelectLeadStatus from "../../components/status";
import SelectLeadLabel from "../../components/label";

// Import for edit lead functionality
import CreateLeadForm from "../leads/createLeadForm";
import LeadDrawerComponent from "../../components/leadDrawer";

const WhatsappInbox: React.FC = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  
  // Redux state selectors
  const conversations = useAppSelector(selectAllConversations);
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const isLoading = useAppSelector(selectConversationsStatus) === 'loading';
  const totalConversations = useAppSelector(selectTotalConversations);
  const lead = useAppSelector(selectLead);
  const searchInChat = useAppSelector(selectSearchInChat);
  const showUserDetails = useAppSelector(selectShowChatDetails);
  const phoneId = useAppSelector(selectWhatsappPhoneId);
  const teamMembers = useAppSelector(selectTeamMembers);
  const isWhatsappIntegrated = useAppSelector(selectIsWhatsappIntegrated);
  const replyContext = useAppSelector(selectReplyContext);
  
  // Redux UI state
  const openForm = useAppSelector(selectOpenForm);
  const showFbButton = useAppSelector(selectShowFbButton);
  const assignLeadHeaderTitle = useAppSelector(selectAssignLeadHeaderTitle);
  const openLeadDrawer = useAppSelector(selectOpenLeadDrawer);

  
  // Local refs
  const boxRef = useRef<HTMLDivElement>(null);
  const listInnerRef = useRef<any>(null);
  const isLoadingOlderRef = useRef(false);

  // 🔥 Ref to store addFiles function from TypingActionAreaRedux (using ref instead of state to avoid function call issues)
  const addFilesRef = useRef<((files: File[]) => void) | undefined>(undefined);

  // Local state (only for non-Redux state)
  const [labelData, setLabelData] = useState<any[]>([]);
  const [userdata, setUserdata] = useState<any>({});
  const [from, setFrom] = useState("");
  const [assignLeadDrawer, setAssignLeadDrawer] = useState(false);
  const [searchMsgText, setSearchMsgText] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [lead2, setLead] = useState<LeadDataI | undefined>();
  const [statusData, setStatusData] = useState<any>({});

  // Status and label popup states
  const [showStatusPopup, setShowStatusPopup] = useState<boolean>(false);
  const [showLabelPopup, setShowLabelPopup] = useState<boolean>(false);
  const [selectedLabel, setSelectedLabel] = useState<Array<string>>([]);
  const [leadLabelData, setLeadLabelData] = useState<any[]>([]);

  // Edit lead drawer states
  const [createNewLeadDrawer, showCreateNewLeadDrawer] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [leadToBeUpdate, setLeadToBeUpdate] = useState<string>("");




// Add these 2 lines with your other selectors
const hasMoreMessages = useAppSelector(selectHasMoreMessages);
const loadingOlderMessages = useAppSelector(selectLoadingOlderMessages);

  // Grid breakpoints
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  // Component initialization
  // In your component's useEffect
useEffect(() => {
  logger.log("WhatsAppInbox - Component initialized with Redux");
  
  // Load status data
  const statusData = loadStatusData();
  setStatusData(statusData);
  
  // Load user data from localStorage
  let local: any = window.localStorage?.getItem("userData");
  if (local) {
    const userData = JSON.parse(local);
    setUserdata(userData);
    // Dispatch to Redux
    dispatch(setCurrentUser(userData));
    logger.log("WhatsAppInbox - User data loaded and dispatched to Redux", { 
      userId: userData._id,
      plan: userData.subscriptionPlanType 
    });
  }
  
  // Check WhatsApp integration
  checkWhatsAppIntegration();
  
  // Fetch conversations via Redux
  dispatch(fetchConversations({ page: 1, refresh: true }));
  logger.log("WhatsAppInbox - Initiated conversation fetch from Redux");
  
  // Component cleanup
  return () => {
    logger.log("WhatsAppInbox - Component unmounting, cleaning up Redux resources");
    if (selectedConversation) {
      dispatch(leaveRoomRequest());
      logger.log("WhatsAppInbox - Dispatched leaveRoomRequest for cleanup");
    }
  };
}, [dispatch]);
  




// Add this ONE useEffect with your other useEffects
useEffect(() => {
  const element = boxRef.current;
  if (!element) return;

  const handleScroll = () => {
   
    
    if (element.scrollTop <= 100 && hasMoreMessages && !loadingOlderMessages && selectedConversation?.convoId) {
      logger.log('🔄 Loading older messages...');
      isLoadingOlderRef.current = true; // ✅ NEW: Flag that we're loading older messages
      dispatch(fetchOlderMessages(selectedConversation.convoId));
    } else {
      logger.log('🔥 SCROLL: Conditions not met:', {
        scrollTopCheck: element.scrollTop <= 100,
        hasMoreMessages,
        notLoading: !loadingOlderMessages,
        hasConvoId: !!selectedConversation?.convoId
      });
    }
  };

  element.addEventListener('scroll', handleScroll, { passive: true });
  return () => element.removeEventListener('scroll', handleScroll);
}, [dispatch, selectedConversation?.convoId, hasMoreMessages, loadingOlderMessages]);
  
  // Check WhatsApp integration
  const checkWhatsAppIntegration = () => {
    try {
      const userIntigrationsList = JSON.parse(
        localStorage.getItem("userData") || ""
      ).userIntegrations;

      const findData = userIntigrationsList.find(
        (item: any) =>
          item?.integration?._id === "646b9286ae8ef73b33caec2e" && item?.isActive
      );

      const hasIntegration = !!findData;
      
      logger.log("WhatsAppInbox - Integration status:", hasIntegration);
      
      // Dispatch to Redux instead of local state
      dispatch(setShowFbButton(!hasIntegration));
    } catch (error) {
      logger.log("WhatsAppInbox - Error checking integration status", error);
      dispatch(setShowFbButton(true));
    }
  };
  

const handleLeadDetailsClick = useCallback(() => {
  logger.log('🔍 Opening lead details from inbox - SIMPLE APPROACH');
  
  if (!lead) {
    logger.log('❌ No lead available');
    toast.error("No lead details available for this conversation");
    return;
  }

  logger.log('✅ Opening drawer for lead:', lead._id);
  // Just open the drawer - LeadDetails component will handle the API call itself
  dispatch(setOpenLeadDrawer(true));
}, [lead, dispatch]);

// In WhatsappInbox.tsx - add to handleConnect function
const handleConnect = useCallback((user: any) => {
  logger.log("🔌 HANDLECONNECT CALLED WITH:", user);
  if (!user) {
    logger.log("WhatsAppInbox - Cannot connect: No user provided");
    return;
  }
  
  logger.log("WhatsAppInbox - Connecting to conversation via Redux", {
    userId: user.id,
    convoId: user.convoId
  });
  
  // Select conversation in Redux
  dispatch(setSelectedConversation(user));
  
  // Use Redux action for socket room joining
  logger.log("🔌 ABOUT TO DISPATCH joinRoomRequest:", {
    username: user?.name,
    room: user?.convoId
  });
  
  dispatch(joinRoomRequest({
    username: user?.name,
    room: user?.convoId
  }));
  
  logger.log("🔌 joinRoomRequest DISPATCHED SUCCESSFULLY");
  
  // Fetch messages for this conversation
  if (user.convoId) {
    dispatch(fetchMessages(user.convoId));
    logger.log("WhatsAppInbox - Dispatched fetchMessages for", user.convoId);
    
    // Add explicit logging for scroll intention
    logger.log("WhatsAppInbox - Will scroll to bottom after messages load");
  }
}, [dispatch]);
  
  // Scroll to bottom helper

const scrollToBottom = useCallback(() => {
  Logger.log("Attempting to scroll to bottom");
  if (boxRef.current) {
    Logger.log("Box ref exists, scrolling to:", {
      scrollHeight: boxRef.current.scrollHeight,
      clientHeight: boxRef.current.clientHeight
    });
    
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      if (boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
        
      }
    }, 100);
  } else {
    
  }
}, [boxRef]);
  
 

/// ✅ REPLACE with this auto-scroll logic that respects older message loading
useEffect(() => {
  // This effect will run whenever selectedConversation changes
  if (selectedConversation) {
    if (!isLoadingOlderRef.current) {
      // ✅ This is for new conversation - scroll to bottom
      logger.log("WhatsAppInbox - Selected conversation changed, scrolling to bottom");
      
      const scrollDelays = [300, 800];
      
      scrollDelays.forEach(delay => {
        setTimeout(() => {
          logger.log(`WhatsAppInbox - Executing scroll after ${delay}ms`);
          scrollToBottom();
        }, delay);
      });
    } else {
      // ✅ This is for older messages - maintain scroll position
      logger.log("WhatsAppInbox - Loading older messages, maintaining scroll position");
      isLoadingOlderRef.current = false; // Reset flag
    }
  }
}, [selectedConversation]);

  // Helper functions
  const addTaskHandler = () => {
    logger.log("WhatsAppInbox - Opening task form via Redux");
    dispatch(setOpenForm("task"));
  };

  const addNoteHandler = () => {
    logger.log("WhatsAppInbox - Opening note form via Redux");
    dispatch(setOpenForm("note")); 
  };

  const handleSearchInChat = (search: boolean) => {
    logger.log("WhatsAppInbox - Setting search in chat:", search);
    dispatch(setSearchInChat(search));
  };

  const handleRefresh = () => {
    logger.log("WhatsAppInbox - Refresh button clicked");
    dispatch(fetchConversations({ page: 1, refresh: true }));
  };
  
  const backHandler = () => {
    logger.log("WhatsAppInbox - Back button pressed via Redux");
    
    if (showUserDetails) {
      dispatch(setShowChatDetails(false));
    } else {
      dispatch(clearSelectedConversation());
    }
  };
  
const handleSetOpenLeadDrawer: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
  logger.log('🔧 handleSetOpenLeadDrawer called with:', value);
  const newValue = typeof value === 'function' ? value(openLeadDrawer) : value; // ← USE openLeadDrawer NOT localOpenLeadDrawer
  logger.log('🔧 Setting openLeadDrawer to:', newValue);
  dispatch(setOpenLeadDrawer(newValue));
};

  // Status and label popup handlers
  const onStatusClick = useCallback(() => {
    logger.log('Status popup opened from WhatsApp inbox');
    logger.log('🔍 DEBUG: Current showStatusPopup state:', showStatusPopup);
    logger.log('🔍 DEBUG: Lead status:', lead?.status);
    if (lead?.status?.length) {
      setSelectedStatus(lead.status[0]);
    }
    setFrom("whatsapp");
    setShowStatusPopup(true);
    logger.log('🔍 DEBUG: Set showStatusPopup to true');
  }, [lead, showStatusPopup]);

  const onLabelClick = useCallback(() => {
    logger.log('Label popup opened from WhatsApp inbox');
    if (lead?.label) {
      setSelectedLabel(lead.label);
    }
    
    // Load label data from localStorage user preferences (like the working components do)
    try {
      // Try the correct localStorage key used by status.tsx and label.tsx
      const userPrefsString = localStorage.getItem("user_preferences");
      logger.log('🔍 DEBUG: user_preferences from localStorage:', userPrefsString);
      
      if (userPrefsString) {
        const userPrefs = JSON.parse(userPrefsString);
        logger.log('🔍 DEBUG: parsed user_preferences:', userPrefs);
        
        const labelPreferences = userPrefs?.labels || [];
        logger.log('🔍 DEBUG: labelPreferences from user_preferences:', labelPreferences);
        
        // Map labels with current selection state
        const labelArray = labelPreferences.map((labelObj: any) => ({
          ...labelObj,
          isLabelChecked: (lead?.label || []).includes(labelObj.value) || false
        }));
        
        logger.log('Loaded label data for popup:', labelArray.length, 'labels', labelArray);
        setLeadLabelData(labelArray);
      } else {
        logger.log('🔍 DEBUG: No user_preferences found in localStorage');
        // Set empty array for now
        setLeadLabelData([]);
      }
    } catch (error) {
      logger.log('Error loading label data:', error);
      setLeadLabelData([]);
    }
    
    setFrom("whatsapp");
    setShowLabelPopup(true);
  }, [lead]);

  const toggleLeadStatus = () => {
    setShowStatusPopup(!showStatusPopup);
    setSelectedStatus("");
  };

  const toggleLeadLabel = () => {
    setShowLabelPopup(!showLabelPopup);
  };

  const onStatusChange = async (e: any) => {
    if (!lead || !e?.target?.value) return;

    const newStatus = e.target.value;
    logger.log('Status change requested:', newStatus);

    // ✅ Just update the local selected status
    // The actual API call happens in the status component when user clicks "Save"
    setSelectedStatus(newStatus);

    // ❌ DO NOT close the modal here - let the status component handle it after API success
    logger.log('Status selection changed to:', newStatus);
  };

  const onLabelChange = async (e: any, name: string) => {
    if (!lead) return;

    const labelValue = name || e?.target?.value;
    if (!labelValue) return;

    logger.log('Label change requested:', labelValue, 'checked:', e?.target?.checked);

    // ✅ Just update the local UI state
    // The actual API call happens in the label component when user clicks "Save"

    // Update the local selected labels for display
    const currentLabels = Array.isArray(selectedLabel) ? selectedLabel : [];
    let updatedLabels;
    if (e?.target?.checked) {
      updatedLabels = currentLabels.includes(labelValue)
        ? currentLabels
        : [...currentLabels, labelValue];
    } else {
      updatedLabels = currentLabels.filter(label => label !== labelValue);
    }

    setSelectedLabel(updatedLabels);

    // Update the leadLabelData to reflect the change in checkbox UI
    setLeadLabelData(prevData =>
      prevData.map(labelObj =>
        labelObj.value === labelValue
          ? { ...labelObj, isLabelChecked: e?.target?.checked }
          : labelObj
      )
    );

    logger.log('Label selection changed to:', updatedLabels);
  };

  const handleLeadUpdate = useCallback((updatedLead: any) => {
    logger.log('Lead updated via popup:', updatedLead);
    dispatch(updateSingleLead(updatedLead));
  }, [dispatch]);

  // Edit lead functionality
  const editLead = useCallback((e: { stopPropagation: () => void }, id: any) => {
    e.stopPropagation();
    logger.log("WhatsAppInbox - Opening edit lead drawer for ID:", id);
    showCreateNewLeadDrawer(true);
    dispatch(setOpenLeadDrawer(false)); // Close lead details drawer
    setIsEditMode(true);
    setLeadToBeUpdate(id);
  }, [dispatch]);

  const toggleEditModal = useCallback(() => {
    showCreateNewLeadDrawer(false);
    setIsEditMode(false);
    setLeadToBeUpdate("");
  }, []);

  const refreshLeadData = useCallback(() => {
    logger.log("WhatsAppInbox - Refreshing lead data after edit");
    if (selectedConversation?.convoId) {
      dispatch(fetchMessages(selectedConversation.convoId));
    }
  }, [dispatch, selectedConversation]);


  



 
  // Handle chat assignment using Redux
  const handleAssignChat = useCallback((userId: string) => {
    if (!selectedConversation) {
      logger.log("WhatsAppInbox - Cannot assign chat: No selected conversation");
      return;
    }
    
    logger.log("WhatsAppInbox - Assigning chat", {
      userId,
      conversationId: selectedConversation._id
    });
    
    dispatch(setLoadingState({ key: 'assignChat', value: true }));
    
    assignChatToUser(selectedConversation._id, userId)
      .then(() => {
        setAssignLeadDrawer(false);
      })
      .finally(() => {
        dispatch(setLoadingState({ key: 'assignChat', value: false }));
      });
  }, [dispatch, selectedConversation]);
  
  // Handle lead assignment - keeping direct API call as per your request
  const handleAssignLead = async (userId: string) => {
    if (!selectedConversation || !lead) {
      logger.log("WhatsAppInbox - Cannot assign lead: Missing data");
      return;
    }
    
    try {
      logger.log("WhatsAppInbox - Assigning lead to user:", userId);
      dispatch(setLoadingState({ key: 'assignLead', value: true }));
      
      await assignLeadToUser(lead._id, userId);
      setAssignLeadDrawer(false);
    } catch (error) {
      // Error is already handled in the utility function with toast
      logger.log("WhatsAppInbox - Error in lead assignment:", error);
    } finally {
      dispatch(setLoadingState({ key: 'assignLead', value: false }));
    }
  };
  

  
  // Chat component (memoized)
  const chat = useMemo(() => {
    return (
      <div style={{ flex: 2, height: "100vh", display: "flex", flexDirection: "column" }} className="col px-0">
        <div className="chat-window-wrapper">
          {/* Chat header - fixed at top with enhanced dropdown */}
          <div className="inboxChatHead">
            <InboxChatHeadRedux
              setSearchInChat={handleSearchInChat}
              addTaskHandler={addTaskHandler}
              addNoteHandler={addNoteHandler}
              onLeadDetailsClick={handleLeadDetailsClick}
              onRefresh={handleRefresh}
            />
          </div>
          
          {/* Chat body - main content with scrolling */}
          <div className="inboxChat">
            <div className="flex-fill position-relative">
              <div className="convoBody" ref={boxRef} id="scrollable-box">
                <ConversationBody
                  scrollToBottom={scrollToBottom}
                  containerRef={boxRef}
                  addFiles={addFilesRef.current}
                />
              </div>
            </div>

            {/* Typing area - fixed at bottom */}
            <div className="typing-area-wrapper">
              <TypingActionAreaRedux
                scrollToBottom={scrollToBottom}
                onAddFilesReady={(fn) => { addFilesRef.current = fn; }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [scrollToBottom, handleSearchInChat]);
  
  // Empty chat screen (memoized)
  const emptyChatScreen = useMemo(() => {
    return (
      <EmptyChatScreen>
        <img src="/assets/img/emptyconvo.png" alt="No chat selected" />
        <h5 className="text-dark fm-gilroy fs-5">
          Select a conversation to start chatting
        </h5>
      </EmptyChatScreen>
    );
  }, []);
  
  // Check subscription plan
  if (userdata?.subscriptionPlanType?.toLowerCase() !== "ultimate") {
    logger.log("WhatsAppInbox - Not on Ultimate plan, showing plan info");
    return (
      <div id="main" className="main">
        <Header />
        <UltimatePlanInfo />
      </div>
    );
  }

  logger.log("WhatsAppInbox - Rendering with Redux state", {
    conversationsCount: conversations?.length,
    selectedConversation: selectedConversation?.id,
    showFbButton,
    showUserDetails,
    openForm,
    showStatusPopup,
    showLabelPopup,
    leadData: !!lead
  });

  // Debug popup states
  if (showStatusPopup) logger.log('🎭 RENDER: Status popup should be visible');
  if (showLabelPopup) logger.log('🎭 RENDER: Label popup should be visible');

  return (
    <div id="main" className="main mt-0 px-0 inboxPage">
      <Header />

      {showFbButton ? (
        <FacebookIntegration
          onSuccess={() => {
            logger.log("WhatsAppInbox - Facebook integration successful");
            dispatch(setShowFbButton(false));
            dispatch(fetchConversations({ page: 1, refresh: true }));
          }}
        />
      ) : (
        <>
          {isLoading && !conversations.length && (
            <LoaderContainer>
              <div className="h-100 d-flex align-items-center justify-content-center">
                <h5 className="text-dark fm-gilroy fs-6 text-center">
                  <Spin size="large" tip="Loading..." spinning={true}></Spin>
                </h5>
              </div>
            </LoaderContainer>
          )}
  
          <MediaPreview />
          <section className="flex-fill">
            <div className="row h-100 m-0">
              <ChatLeftMenuRedux 
  onConnect={handleConnect}  // ← ADD THIS
  listInnerRef={listInnerRef} 
/>
              
              {selectedConversation ? chat : <div style={{ flex: 2 }}>{emptyChatScreen}</div>}
            </div>
          </section>
        </>
      )}
  
      {/* Task Drawer */}
      <TaskDrawer
        openWithHeader={!!lead && !!openForm}
        setOpenWithHeader={() => {
          logger.log("WhatsAppInbox - Closing task/note drawer via Redux");
          dispatch(setOpenForm(null));
        }}
        onTaskDelete={() => {}}
        showDelete={false}
        drawerTitle={`Add ${openForm || ""}`}
        size="xs"
      >
        {openForm == "task" ? (
          <CreateTaskForm
            leadId={lead?._id}
            updateTaskValue={{}}
            action="add"
            status={""}
            drawerClose={() => {
              dispatch(setOpenForm(null));
            }}
            fetchTaskStatusFollowUps={() => {}}
          />
        ) : (
          <CreateNoteForm
            leadIds={[{ id: lead?._id }]}
            handleNotesSubmit={() => {
              dispatch(setOpenForm(null));
            }}
          />
        )}
      </TaskDrawer>
  
      {/* Assign Lead/Chat Drawer */}
      <GlobalDrawer
        openWithHeader={assignLeadDrawer}
        setOpenWithHeader={() => {
          logger.log("WhatsAppInbox - Closing assign lead/chat drawer");
          setAssignLeadDrawer(false);
        }}
        drawerTitle={assignLeadHeaderTitle || "Assign Lead"}
        size="xs"
      >
        <label className="form-label">Select team member</label>
        <div>
          {teamMembers?.map((item: any, ix: number) => (
            <button
              key={ix}
              onClick={() => {
                setAssignLeadDrawer(false);
                
                if (assignLeadHeaderTitle === "Assign Chat") {
                  logger.log("WhatsAppInbox - Assigning chat to", item.label);
                  handleAssignChat(item.value);
                } else {
                  logger.log("WhatsAppInbox - Assigning lead to", item.label);
                  handleAssignLead(item.value);
                }
              }}
              className="d-block bg-white w-100 p-3 rounded mb-2"
              style={{ margin: "8px 0px", textAlign: "left" }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </GlobalDrawer>
      
      
  
    
      {/* Lead Details Drawer - Add this new section */}
    {lead && openLeadDrawer && (
  <DrawerComponent
    openWithHeader={openLeadDrawer}
    setOpenWithHeader={handleSetOpenLeadDrawer}
    customHeader={false}
    drawerTitle="Lead Details"
    size="lg"
    className="lead_details"
  >
    <LeadDetails
      key={`lead-details-${lead._id}-${openLeadDrawer}`}
      _id={lead._id}
      name={lead.name}
      integration={lead.integration}
      customSource={lead.customSource}
      createdAt={lead.createdAt}
      status={lead.status}
      saleValue={lead.saleValue}
      website={lead.website}
      label={lead.label}
      // ✅ PROPER HANDLERS - Enable status/label popups and edit functionality
      deleteLeadFromDrawer={() => {}}
      copyLeadToList={() => {}}
      moveLeadToList={() => {}}
      onLabelClick={onLabelClick}
      onStatusClick={onStatusClick}
      setFrom={setFrom}
      handleEditModal={() => {}}
      handleLeadDetailsModalOpen={() => {}}
      onLeadFileUpload={() => {}}
      editLead={editLead}
      leadFileDelete={() => {}}
    />
  </DrawerComponent>
)}

      {/* Edit Lead Drawer */}
      <LeadDrawerComponent
        openWithHeader={createNewLeadDrawer}
        setOpenWithHeader={toggleEditModal}
        drawerTitle={!isEditMode ? "Add New Lead" : "Update Lead"}
        size="sm"
      >
        <CreateLeadForm
          isDetailsBox={false}
          isEditMode={isEditMode}
          showCreateNewLeadDrawer={showCreateNewLeadDrawer}
          getLeads={refreshLeadData}
          leadToBeUpdate={leadToBeUpdate}
          handleDrawerClick={toggleEditModal}
          selectedLeadId={lead?.leadListId || "0"}
          leadList={[]}
        />
      </LeadDrawerComponent>

      {/* Status Popup */}
      <SelectLeadStatus
        showStatusPopup={showStatusPopup}
        toggleLeadStatus={toggleLeadStatus}
        onStatusChange={onStatusChange}
        onLeadUpdate={handleLeadUpdate}
        selectedStatus={selectedStatus}
        setShowStatusPopup={setShowStatusPopup}
        setOpenWithHeader={setShowStatusPopup}
        from={from}
        id={lead?._id || ""}
        leadList=""
        getLeads={() => {
          logger.log('Status popup getLeads called - refreshing data');
          // Optionally refresh lead data here
        }}
        updateEditedRow={(newData: any) => {
          logger.log('Status popup updateEditedRow called with:', newData);
          dispatch(updateSingleLead(newData));
        }}
      />

      {/* Label Popup */}
      <SelectLeadLabel
        showLabelPopup={showLabelPopup}
        toggleLeadLabel={toggleLeadLabel}
        onLabelChange={onLabelChange}
        selectedLabel={selectedLabel}
        onLeadUpdate={handleLeadUpdate}
        setShowLabelPopup={setShowLabelPopup}
        setOpenWithHeader={setShowLabelPopup}
        LabelData={leadLabelData}
        from={from}
        id={lead?._id || ""}
        leadList=""
        getLeads={() => {
          logger.log('Label popup getLeads called - refreshing data');
          // Optionally refresh lead data here
        }}
        updateEditedRow={(newData: any) => {
          logger.log('Label popup updateEditedRow called with:', newData);
          dispatch(updateSingleLead(newData));
        }}
      />
    </div>
  );
};

export default WhatsappInbox;